import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import AppWrapper from "../AppWrapper";
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  InlineGrid,
  Text,
  Spinner,
  Banner,
  Button,
  TextField,
  Badge,
} from "@shopify/polaris";
import supabase from "../../supabase-client";
import { levelsText, StatusBadge, fmtMoney, fmtDateTime, SLOTS } from "./washUtil";

const Photo = ({ url, label }) =>
  url ? (
    <a href={url} target="_blank" rel="noopener noreferrer">
      <img
        src={url}
        alt={label}
        style={{ width: "100%", aspectRatio: "4 / 3", objectFit: "cover", borderRadius: 8 }}
      />
    </a>
  ) : (
    <div
      style={{
        width: "100%",
        aspectRatio: "4 / 3",
        borderRadius: 8,
        background: "var(--p-color-bg-fill-secondary)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Text tone="subdued" variant="bodySm">Sin foto</Text>
    </div>
  );

function Wash() {
  const { washId } = useParams();
  const [wash, setWash] = useState(null);
  const [photos, setPhotos] = useState({}); // slot → { before, after } signed urls
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const { data: w } = await supabase
      .from("car_washes")
      .select(
        "*, branch:branches(name), car:cars(id, plate, color, model:car_models(name, show_as, brand:car_brands(name))), staff:users!staff_id(name), finisher:users!finished_by(name)"
      )
      .eq("id", washId)
      .single();
    const { data: ps } = await supabase
      .from("car_wash_photos")
      .select("phase, slot, file_url")
      .eq("wash_id", washId);
    const bySlot = {};
    await Promise.all(
      (ps || []).map(async (p) => {
        const { data } = await supabase.storage.from("wash-photos").createSignedUrl(p.file_url, 3600);
        bySlot[p.slot] = { ...(bySlot[p.slot] || {}), [p.phase]: data?.signedUrl || null };
      })
    );
    setWash(w);
    setPhotos(bySlot);
    setNote(w?.review_note || "");
    setLoading(false);
  }, [washId]);

  useEffect(() => {
    load();
  }, [load]);

  const review = async (status) => {
    setSaving(status);
    setError(null);
    const { error: e } = await supabase.rpc("review_wash", {
      p_id: Number(washId),
      p_status: status,
      p_note: note,
    });
    setSaving(null);
    if (e) setError(e.message);
    else load();
  };

  if (loading || !wash) {
    return (
      <AppWrapper>
        <Page title="Lavado" backAction={{ url: "/lavados" }}>
          {loading ? <Spinner size="small" /> : <Text>No encontrado</Text>}
        </Page>
      </AppWrapper>
    );
  }

  const ai = wash.ai_checks || {};
  const slotNotes = Object.fromEntries((ai.slots || []).map((s) => [s.slot, s]));
  const carName = wash.car?.model?.show_as || wash.car?.model?.name || "";
  const slots = SLOTS.filter(([key]) => photos[key]);

  return (
    <AppWrapper>
      <Page
        title={`Lavado · ${wash.car?.plate || ""}`}
        subtitle={`${carName} · ${wash.branch?.name || "Sin sucursal"} · ${fmtDateTime(wash.started_at)}`}
        titleMetadata={<StatusBadge status={wash.review_status} />}
        backAction={{ url: "/lavados" }}
      >
        <BlockStack gap="400">
          <InlineGrid columns={{ xs: 1, md: 3 }} gap="400">
            <Card>
              <BlockStack gap="100">
                <Text tone="subdued" variant="bodySm">Tipo</Text>
                <Text variant="headingMd">{levelsText(wash.levels)}</Text>
                <Text variant="bodySm">Pago: {fmtMoney(wash.price_total)}</Text>
                <Text variant="bodySm">
                  Empezó {wash.staff?.name || "-"}
                  {wash.finisher && wash.finished_by !== wash.staff_id
                    ? ` · terminó ${wash.finisher.name}`
                    : ""}
                </Text>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="100">
                <Text tone="subdued" variant="bodySm">Duración</Text>
                <Text variant="headingMd">
                  {wash.duration_min != null ? `${wash.duration_min} min` : "En curso"}
                </Text>
                <Text variant="bodySm">
                  {fmtDateTime(wash.started_at)} → {wash.finished_at ? fmtDateTime(wash.finished_at) : "…"}
                </Text>
              </BlockStack>
            </Card>
            <Card>
              <BlockStack gap="100">
                <Text tone="subdued" variant="bodySm">Calificación IA</Text>
                <Text variant="headingMd">
                  {wash.ai_score != null
                    ? `${wash.ai_score}/10`
                    : wash.ai_status === "error"
                    ? "No disponible"
                    : wash.ai_status === "waiting"
                    ? "Al terminar"
                    : "Calificando…"}
                </Text>
                {ai.same_car === false && <Badge tone="critical">¿Otro auto?</Badge>}
              </BlockStack>
            </Card>
          </InlineGrid>

          {(wash.ai_summary || ai.flags?.length > 0) && (
            <Card>
              <BlockStack gap="200">
                {wash.ai_summary && <Text>{wash.ai_summary}</Text>}
                {ai.flags?.length > 0 && (
                  <Banner tone="warning" title="Por qué se marcó para revisión">
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {ai.flags.map((f, i) => (
                        <li key={i}>{f}</li>
                      ))}
                    </ul>
                  </Banner>
                )}
                {(ai.issues || []).length > 0 && (
                  <Text tone="subdued" variant="bodySm">Observaciones: {ai.issues.join(" · ")}</Text>
                )}
                {wash.notes && <Text variant="bodySm">Notas del lavador: {wash.notes}</Text>}
              </BlockStack>
            </Card>
          )}

          {!["open", "cancelled"].includes(wash.review_status) && (
          <Card>
            <BlockStack gap="300">
              <Text variant="headingSm">Revisión</Text>
              {error && <Banner tone="critical">{error}</Banner>}
              {wash.reviewed_at && (
                <Text variant="bodySm" tone="subdued">
                  Revisado el {fmtDateTime(wash.reviewed_at)}
                </Text>
              )}
              <TextField
                label="Nota (opcional)"
                value={note}
                onChange={setNote}
                multiline={2}
                autoComplete="off"
              />
              <InlineStack gap="200">
                <Button
                  variant="primary"
                  onClick={() => review("approved")}
                  loading={saving === "approved"}
                  disabled={!!saving}
                >
                  Aprobar (se paga)
                </Button>
                <Button
                  tone="critical"
                  onClick={() => review("rejected")}
                  loading={saving === "rejected"}
                  disabled={!!saving}
                >
                  Rechazar (no se paga)
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>
          )}

          {slots.map(([key, label]) => {
            const n = slotNotes[key];
            return (
              <Card key={key}>
                <BlockStack gap="200">
                  <InlineStack align="space-between">
                    <Text variant="headingSm">{label}</Text>
                    {n && (
                      <Text variant="bodySm" tone={n.comparable ? "subdued" : "critical"}>
                        {n.comparable
                          ? `Suciedad antes ${n.before_dirt}/10 · limpieza después ${n.after_clean}/10`
                          : "No comparable"}
                      </Text>
                    )}
                  </InlineStack>
                  <InlineGrid columns={2} gap="200">
                    <BlockStack gap="100">
                      <Text variant="bodySm" tone="subdued">Antes</Text>
                      <Photo url={photos[key]?.before} label={`${label} antes`} />
                    </BlockStack>
                    <BlockStack gap="100">
                      <Text variant="bodySm" tone="subdued">Después</Text>
                      <Photo url={photos[key]?.after} label={`${label} después`} />
                    </BlockStack>
                  </InlineGrid>
                  {n?.note && <Text variant="bodySm" tone="subdued">{n.note}</Text>}
                </BlockStack>
              </Card>
            );
          })}
        </BlockStack>
      </Page>
    </AppWrapper>
  );
}

export default Wash;
